import { z } from 'zod';

/**
 * Response envelopes and primitive field schemas shared by every resource.
 * Success bodies are `{data}`; lists add `has_more` and `next_cursor`.
 */

export function itemEnvelope<S extends z.ZodType>(schema: S) {
  return z.object({ data: schema });
}

export function pageEnvelope<S extends z.ZodType>(schema: S) {
  return z.object({
    data: z.array(schema),
    has_more: z.boolean(),
    next_cursor: z.string().nullable(),
  });
}

export interface Page<T> {
  data: T[];
  has_more: boolean;
  next_cursor: string | null;
}

/** Cursor pagination parameters accepted by every list endpoint (limit ≤ 100). */
export interface PageParams {
  limit?: number;
  cursor?: string | null;
}

/** UUIDv7 ids are opaque to the client. */
export const zId = z.string().min(1);
/** RFC 3339 UTC instant. */
export const zInstant = z.string();
/** YYYY-MM-DD business date. */
export const zDate = z.string();
/** Integer minor units. */
export const zMinor = z.number().int();
/** ISO 4217 code. */
export const zCurrency = z.string().length(3);
/**
 * Decimal string (unit prices, quantities, rates). Numbers are accepted and
 * stringified defensively; the API contract is strings.
 */
export const zDecimal = z.union([z.string(), z.number()]).transform((value) => String(value));

export const zMetadata = z.record(z.string(), z.unknown());

export const zAddress = z
  .object({
    line1: z.string().nullish(),
    line2: z.string().nullish(),
    city: z.string().nullish(),
    region: z.string().nullish(),
    postal_code: z.string().nullish(),
    country: z.string().nullish(),
  })
  .partial();

export type Address = z.infer<typeof zAddress>;

/** Unpaginated collections (`{data: [...]}`) such as contract versions and amendments. */
export function listEnvelope<S extends z.ZodType>(schema: S) {
  return z.object({ data: z.array(schema) });
}
